import { useCallback, useEffect, useRef, useState } from 'react';

// Chrome fires `beforeinstallprompt` once, early in the page's life — keep the
// captured event at module scope so hook instances mounted later (e.g. the
// Help page's "Get the app" card) can still trigger the native dialog.
let capturedInstallPrompt = null;

// How long a dismissal keeps the banner hidden before we gently re-offer it
const SNOOZE_DAYS = 30;
// Small delay so the banner slides in after the page has settled, instead of
// competing with the initial render
const SHOW_DELAY_MS = 2500;

const DISMISSED_AT_KEY = 'pwaInstallDismissedAt';
const INSTALLED_KEY = 'pwaInstalled';

const isIosDevice = () => {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ masquerades as macOS but is the only "Mac" with a touch screen
  const isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return /iphone|ipad|ipod/i.test(ua) || isIpadOs;
};

// On iOS every browser is WebKit, but only Safari (and iOS 16.4+ third-party
// browsers) expose "Add to Home Screen" — we use this to tailor instructions
const isIosSafari = () => !/crios|fxios|edgios|opios|opt\//i.test(window.navigator.userAgent);

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isSnoozed = () => {
  const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_KEY));
  if (!dismissedAt) return false;
  return Date.now() - dismissedAt < SNOOZE_DAYS * 24 * 60 * 60 * 1000;
};

/**
 * Everything needed to promote installing the portal as an app:
 * - Android/Chrome: captures `beforeinstallprompt` so we can trigger the
 *   native install dialog from our own UI (`promptInstall`).
 * - iOS: no install API exists, so we only signal that manual
 *   "Add to Home Screen" instructions should be shown (`isIos`).
 * - Hides itself when already installed/running standalone, and remembers
 *   dismissals for SNOOZE_DAYS via localStorage.
 */
const usePwaInstall = () => {
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [canPromptInstall, setCanPromptInstall] = useState(() => !!capturedInstallPrompt);
  const showTimerRef = useRef(null);

  const isIos = isIosDevice();
  const isAndroid = /android/i.test(window.navigator.userAgent);

  const isEligible = useCallback(
    () =>
      (isIos || isAndroid) &&
      !isStandalone() &&
      localStorage.getItem(INSTALLED_KEY) !== 'true' &&
      !isSnoozed(),
    [isIos, isAndroid]
  );

  const scheduleShow = useCallback(() => {
    if (showTimerRef.current) return;
    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null;
      setIsBannerVisible(true);
    }, SHOW_DELAY_MS);
  }, []);

  useEffect(() => {
    // Eligibility (incl. the dismissal snooze) only gates the BANNER. The
    // install prompt listener always runs so a snoozed member can still
    // install from the explicit "Get the app" entry point (member feedback
    // #839: "once closed, there was no way to bring it back").
    const bannerEligible = isEligible();

    // iOS never fires beforeinstallprompt, so eligibility alone shows the banner
    if (bannerEligible && isIos) scheduleShow();

    const handleBeforeInstallPrompt = (event) => {
      // Suppress Chrome's mini-infobar; we present our own banner instead
      event.preventDefault();
      capturedInstallPrompt = event;
      setCanPromptInstall(true);
      if (bannerEligible) scheduleShow();
    };

    const handleAppInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, 'true');
      capturedInstallPrompt = null;
      setCanPromptInstall(false);
      setIsBannerVisible(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
  }, [isEligible, isIos, scheduleShow]);

  // Triggers the native Android/Chrome install dialog
  const promptInstall = useCallback(async () => {
    const deferredPrompt = capturedInstallPrompt;
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    // The captured event is single-use either way
    capturedInstallPrompt = null;
    setCanPromptInstall(false);
    if (outcome === 'accepted') {
      localStorage.setItem(INSTALLED_KEY, 'true');
    } else {
      localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    }
    setIsBannerVisible(false);
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    setIsBannerVisible(false);
  }, []);

  // Device could run the app but isn't yet — ignores the banner snooze, so
  // explicit entry points (Help page "Get the app") stay available after a
  // dismissal.
  const canInstall =
    (isIos || isAndroid) &&
    !isStandalone() &&
    localStorage.getItem(INSTALLED_KEY) !== 'true';

  return {
    isBannerVisible,
    isIos,
    isIosSafari: isIos && isIosSafari(),
    canInstall,
    canPromptInstall,
    promptInstall,
    dismiss,
  };
};

export default usePwaInstall;
